import { JSX } from 'react';

import { AtIcon } from '../at-icon';
import { EIcons } from '../at-icon/icons';
import { arrowClasses } from './at-arrow.variants';
import classNames from 'classnames';
import { IAtarrowProps } from './at-arrow.types';

const getArrowIcon = (direction: 'prev' | 'next' | 'restart') => {
  const icons = {
    next: EIcons.RIGHT_ARROW,
    prev: EIcons.LEFT_ARROW,
    restart: EIcons.RESTART_ARROW,
  };

  return icons[direction];
};

/**
 * `AtArrow` is a directional navigation button used inside the `AtCarousel` component.
 *
 * It displays an arrow icon (left, right, or restart) and triggers a callback when clicked,
 * unless disabled. It supports theme-aware styling and subtle motion on hover.
 *
 * Accessibility is enforced using both `disabled` and `aria-disabled` attributes.
 *
 */

export const AtArrow = ({
  direction = 'next',
  isDisabled = false,
  onClick,
  theme = 'light',
}: IAtarrowProps): JSX.Element => {
  return (
    <button
      aria-label={`${direction} nav button`}
      data-theme={theme}
      data-testid={`arrow-${direction}`}
      aria-disabled={isDisabled}
      className={classNames(arrowClasses({ isDisabled }))}
      disabled={isDisabled}
      onClick={!isDisabled ? onClick : undefined}
    >
      <div
        className={classNames(
          'relative flex h-full w-full items-center justify-center transition-transform duration-150 motion-reduce:hover:transform-none',
          {
            'hover:translate-x-1': direction === 'next',
            'hover:-translate-x-1': direction === 'prev',
          },
        )}
      >
        <AtIcon
          data-theme={theme === 'dark' ? 'light' : 'dark'}
          color="var(--color-ui-knockout-primary)"
          className="bg-transparent"
          icon={getArrowIcon(direction)}
        />
      </div>
    </button>
  );
};
